import React, { type FC, type PropsWithChildren } from 'react'
import { type SxProps, TableCell, TableRow, type Theme } from '@mui/material'

import { createHandlerWithoutPropagation } from '../../utils'
import { TableCheckbox } from './Checkbox'
import { useColumn, useColumnNames, useOnRowClick, useSelection } from './State'
import type { Columns, Line } from './Types'

const baseCellStyle: SxProps<Theme> = { wordBreak: 'break-all' }
const clickableRowStyle: SxProps<Theme> = { cursor: 'pointer' }

const Cell = <L extends Line,>({ name, row, index }: PropsWithChildren<{ name: keyof Columns<L>, row: L, index: number }>): ReturnType<FC> => {
    const { customCellRender = undefined, sx = null } = useColumn<L, keyof Columns<L>>(name)

    return (
        <TableCell sx={{ ...baseCellStyle, ...sx }}>
            {customCellRender ? customCellRender(row[name], row, index) : row[name] === null ? '-' : <>{row[name]}</>}
        </TableCell>
    )
}

export const Row = <L extends Line,>({ index, row }: PropsWithChildren<{ index: number, row: L }>): ReturnType<FC> => {
    const names = useColumnNames()
    const onRowClick = useOnRowClick()

    const [selected, toggleSelection] = useSelection(row)

    return (
        <TableRow
            key={`table-row-${index}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            hover={Boolean(onRowClick)}
            sx={onRowClick && clickableRowStyle}
        >
            {selected !== null && (
                <TableCell key={`checkbox-${index}`} padding="checkbox">
                    <TableCheckbox data-testid={`checkbox-${index}`} checked={selected} onClick={createHandlerWithoutPropagation(toggleSelection)} />
                </TableCell>
            )}

            {names.map((name, colIndex) => (
                <Cell key={`table-body-${index}-${colIndex}`} name={name} row={row} index={index} />
            ))}
        </TableRow>
    )
}
